import { Alert, Button, Empty, Space, Table } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { MdOutlineAdd } from 'react-icons/md'

import { titlesOf } from '@/Containers/TransportationSection/Components'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { formatDateTimeUk, renderUsersView } from '@/Ship/Helpers/fieldViews'
import { CheckGlobalResourceAccessTask } from '@/Ship/Tasks/CheckGlobalResourceAccessTask'

import { REQUEST_DRAWER_WIDTH, REQUEST_RESOURCE } from '../Contracts/IRequest'
import { REQUEST_FIELD_LABELS as L } from '../Contracts/RequestFieldLabels'
import { ClientRequestsStore } from '../Stores/ClientRequestsStore'
import { RequestStatus } from './RequestCells'

import type { RequestRecord } from '../Contracts/IRequest'
import type { TableColumnsType } from 'antd'
import type { ReactNode } from 'react'

/** Клієнт, у картці якого стоїть список: значення його форми. */
export interface ClientRequestsOwner {
    uuid: string
    '@id'?: string
    title?: string
}

const PAGE_SIZE = 10

/**
 * Колонки — ті самі, що в реєстрі, без «Клієнт»: він дублював би власника
 * картки (легасі вкладки — `RequestCrud` з префільтром клієнта).
 */
const COLUMNS: TableColumnsType<RequestRecord> = [
    { title: L.id, key: 'id', dataIndex: 'id', width: 70 },
    {
        title: L.status,
        key: 'status',
        render: (_: unknown, row) => <RequestStatus status={row.status} countOffers={row.countOffers} />,
    },
    { title: L.loadingCountries, key: 'loadingCountries', render: (_: unknown, row) => titlesOf(row.loadingCountries) || '—' },
    {
        title: L.unloadingCountries,
        key: 'unloadingCountries',
        render: (_: unknown, row) => titlesOf(row.unloadingCountries) || '—',
    },
    { title: L.typesCars, key: 'typesCars', render: (_: unknown, row) => titlesOf(row.typesCars) || '—' },
    {
        title: L.responsible,
        key: 'responsible',
        render: (_: unknown, row): ReactNode => (row.responsible ? renderUsersView(row.responsible) : '—'),
    },
    { title: L.createdAt, key: 'createdAt', render: (_: unknown, row) => formatDateTimeUk(row.createdAt) || '—' },
]

/**
 * Запити клієнта в його картці (`SPEC-025` §3.5, С28.1): список із префільтром
 * клієнта, без панелі фільтрів і канбану; «Додати» підставляє клієнта.
 *
 *  - **власна таблиця**, а не ядровий список: вкладений список чужого ресурсу
 *    підхоплює контекст картки, і збереження пішло б на адресу клієнта
 *    (так само зроблені «Справи» клієнта). Картку запиту відкриває
 *    `OpenResourceCardTask` — другою шухлядою поверх клієнта;
 *  - **«Додати»** — за колекційним `create` запитів, а не за правом на клієнта;
 *  - список перечитується після закриття картки запиту.
 */
export const ClientRequestsList = observer(({ client }: { client: ClientRequestsOwner }) => {
    const [store] = useState(() => new ClientRequestsStore(PAGE_SIZE))

    useEffect(() => {
        store.bind(client.uuid)
    }, [store, client.uuid])

    const reload = (): void => {
        void store.reload()
    }

    const openCard = (mode: 'create' | 'view', uuid?: string): void => {
        const clientRef = {
            uuid: client.uuid,
            '@id': client['@id'] ?? `/services/clients/${client.uuid}`,
            ...(client.title ? { title: client.title } : {}),
        }

        OpenResourceCardTask({
            resource: REQUEST_RESOURCE,
            mode,
            displayMode: 'drawer',
            drawer: { width: REQUEST_DRAWER_WIDTH },
            ...(uuid ? { primaryFieldValue: uuid } : {}),
            ...(mode === 'create' ? { initialValues: { client: clientRef } } : {}),
            onSave: async () => reload(),
            onClose: reload,
        })
    }

    const canAdd = CheckGlobalResourceAccessTask(REQUEST_RESOURCE, 'create')

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {canAdd ? (
                <Button type="primary" icon={<MdOutlineAdd />} onClick={() => openCard('create')}>
                    Додати запит
                </Button>
            ) : null}

            {store.failed ? <Alert type="error" showIcon title="Не вдалося завантажити запити." /> : null}

            {!store.isLoading && store.items.length === 0 && !store.failed ? (
                <Empty description="Запитів ще немає." />
            ) : (
                <Table<RequestRecord>
                    size="small"
                    rowKey={(row) => String(row.uuid)}
                    columns={COLUMNS}
                    dataSource={store.items}
                    loading={store.isLoading}
                    pagination={
                        store.total > store.pageSize
                            ? {
                                  current: store.page,
                                  pageSize: store.pageSize,
                                  total: store.total,
                                  showSizeChanger: false,
                                  onChange: (page) => store.setPage(page),
                              }
                            : false
                    }
                    onRow={(row) => ({
                        style: { cursor: 'pointer' },
                        onClick: () => {
                            if (typeof row.uuid === 'string') openCard('view', row.uuid)
                        },
                    })}
                />
            )}
        </Space>
    )
})
